import React, { PropsWithChildren, useRef } from 'react';
import Form, { FormProps } from './Form';
import { FormItem, FormListContextProvider } from '../../input/FormListTab/contexts/FormListContext';
import { convertFieldError } from '../../../lib/utils/form';
import { FieldError } from '../../../types/common/form';

interface FormWithTabProps<FormValues extends object> extends FormProps<FormValues> {
  name: string;
}

const FormWithTab = <FormValues extends object>(props: PropsWithChildren<FormWithTabProps<FormValues>>) => {
  const { name, onFinish, onFinishFailed, scrollToFirstError, children, ...rest } = props;
  const tabRef = useRef<{ formList: FormItem[] }>();

  const validateTab = () => {
    /**
     * 탭에 있는 하위 폼들을 순회하면서 유효성 검사를 진행하고
     * (scrollToFirstError 여부에 따라 스크롤 동작 처리)
     * 첫번째 에러가 발생한 탭의 id를 firstErrorTabId에 저장한 후에
     * 유효성이 끝나면 해당 탭 활성화
     * */
    return tabRef.current?.formList.flatMap(({ form, id }) => {
      let errors: FieldError[] = [];
      if (scrollToFirstError) {
        const scrollOption = scrollToFirstError === true ? { offsetBottom: 100 } : scrollToFirstError;
        form.validateFieldsAndScroll(
          { scroll: scrollOption },
          (err: { [fieldName: string]: { errors: FieldError[] } }) => {
            if (err) errors = convertFieldError(err);
          },
        );
      } else {
        form.validateFields((err: { [fieldName: string]: { errors: FieldError[] } }) => {
          if (err) errors = convertFieldError(err);
        });
      }

      return errors.map((error) => ({ ...error, id }));
    });
  };

  const handleFinish = (value: FormValues) => {
    const errors = validateTab();

    if (errors?.length) return onFinishFailed && onFinishFailed([{ field: name, errors }]);

    onFinish && onFinish(value);
  };

  const handleFinishFailed = (formErrors: FieldError[]) => {
    const errors = validateTab() || [];
    onFinishFailed && onFinishFailed([...formErrors, { field: name, errors }]);
  };

  return (
    <FormListContextProvider ref={tabRef}>
      <Form
        {...rest}
        onFinish={handleFinish}
        onFinishFailed={handleFinishFailed}
        scrollToFirstError={scrollToFirstError}
      >
        {children}
      </Form>
    </FormListContextProvider>
  );
};

export default FormWithTab;
